// src/components/RegionOptionalSlices.js

import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    Alert,
    Card,
    CardContent,
    CircularProgress,
    Slider
} from '@mui/material';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import api from '../api';
import { convertPdfToImages } from '../utils/pdfUtils';

import {
    REGION_FORM_STYLES,
    DEFAULT_CROP,
} from './RegionConstants';
import {
    canHaveMentalForamen,
    canHaveLingualCanal,
    canHaveNasopalatine,
    getDefaultSliceText,
    generateSliceFilename
} from './RegionUtils';

/**
 * RegionOptionalSlices Component
 * 
 * Handles optional slice functionality for Mental Foramen, Lingual Canal,
 * and Nasopalatine Canal based on region-specific requirements.
 */
const RegionOptionalSlices = ({
    regionNumber,
    crop = DEFAULT_CROP,
    completedCrop = null,
    optionalSliceData = {},
    onCropChange,
    onCropComplete,
    onOptionalSliceChange,
    disabled = false
}) => {
    // Refs for image processing
    const [imageRef, setImageRef] = useState(null);
    const canvasRef = useRef(null);

    // Local state for error handling
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);

    // New state for the slice-specific PDF and images
    const [slicePdfFile, setSlicePdfFile] = useState(null);
    const [sliceImages, setSliceImages] = useState([]);
    const [isConverting, setIsConverting] = useState(false);
    const [conversionError, setConversionError] = useState(null);
    const [slicePreviewUrl, setSlicePreviewUrl] = useState(null);
    const [currentSliceImageIndex, setCurrentSliceImageIndex] = useState(0);


    // Determine which optional slices are available for this region
    const canShowMentalForamen = canHaveMentalForamen(regionNumber);
    const canShowLingualCanal = canHaveLingualCanal(regionNumber);
    const canShowNasopalatine = canHaveNasopalatine(regionNumber);

    useEffect(() => {
        // Clean up the object URL on unmount
        return () => {
            if (slicePreviewUrl) {
                URL.revokeObjectURL(slicePreviewUrl);
            }
        };
    }, [slicePreviewUrl]);

    const handlePdfFileChange = async (event) => {
        const file = event.target.files[0];
        if (file) {
            setSlicePdfFile(file);
            setSliceImages([]);
            setConversionError(null);
            setIsConverting(true);
            setCurrentSliceImageIndex(0);

            try {
                const images = await convertPdfToImages(file);
                setSliceImages(images);
            } catch (err) {
                console.error("Error extracting slice PDF images:", err);
                setConversionError("An error occurred during PDF conversion in the browser.");
            } finally {
                setIsConverting(false);
            }
        }
    };

    const handleSliderChange = (event, newValue) => {
        setCurrentSliceImageIndex(newValue);
    };

    /**
     * Handle image load event
     */
    const onImageLoad = useCallback((e) => {
        const image = e.target;
        setImageRef(image);
        setError(null);
        console.log(`RegionOptionalSlices ${regionNumber}: Image loaded for slicing`);
    }, [regionNumber]);

    /**
     * Get cropped image from canvas
     */
    const getCroppedImage = useCallback((image, cropData) => {
        const canvas = canvasRef.current;

        if (!canvas || !image) {
            console.error(`RegionOptionalSlices ${regionNumber}: Canvas or image not available`);
            return Promise.resolve(null);
        }

        if (!cropData || cropData.width <= 0 || cropData.height <= 0) {
            console.error(`RegionOptionalSlices ${regionNumber}: Invalid crop data`, cropData);
            return Promise.resolve(null);
        }

        try {
            const scaleX = image.naturalWidth / image.width;
            const scaleY = image.naturalHeight / image.height;

            canvas.width = cropData.width * scaleX;
            canvas.height = cropData.height * scaleY;

            const ctx = canvas.getContext('2d');
            if (!ctx) {
                console.error(`RegionOptionalSlices ${regionNumber}: Could not get canvas context`);
                return Promise.resolve(null);
            }

            ctx.drawImage(
                image,
                cropData.x * scaleX,
                cropData.y * scaleY,
                cropData.width * scaleX,
                cropData.height * scaleY,
                0, 0,
                canvas.width,
                canvas.height
            );

            return new Promise((resolve) => {
                canvas.toBlob((blob) => {
                    resolve(blob);
                }, 'image/png', 1);
            });
        } catch (error) {
            console.error(`RegionOptionalSlices ${regionNumber}: Error in getCroppedImage:`, error);
            return Promise.resolve(null);
        }
    }, [regionNumber]);

    /**
     * Handle optional slice image capture
     */
    const handleOptionalSlice = useCallback(async (sliceType) => {
        if (!imageRef || (!completedCrop && !crop)) {
            setError('Please select an area to crop first');
            return;
        }

        const cropToUse = completedCrop || crop;

        try {
            setError(null);
            setProcessing(true);

            console.log(`RegionOptionalSlices ${regionNumber}: Capturing ${sliceType} slice`);

            const croppedBlob = await getCroppedImage(imageRef, cropToUse);

            if (!croppedBlob) {
                setError('Failed to create slice image');
                return;
            }

            const filename = generateSliceFilename(sliceType, regionNumber);
            const croppedFile = new File([croppedBlob], filename, { type: 'image/png' });

            // Create a preview URL
            if (slicePreviewUrl) {
                URL.revokeObjectURL(slicePreviewUrl);
            }
            setSlicePreviewUrl(URL.createObjectURL(croppedFile));


            console.log(`RegionOptionalSlices ${regionNumber}: ${sliceType} slice captured successfully`, {
                filename,
                fileSize: croppedFile.size
            });

            if (onOptionalSliceChange) {
                onOptionalSliceChange('sliceImageFile', croppedFile);
                onOptionalSliceChange('sliceType', sliceType);
            }
        } catch (error) {
            console.error(`RegionOptionalSlices ${regionNumber}: Slice capture failed:`, error);
            setError('Failed to capture slice image. Please try again.');
        } finally {
            setProcessing(false);
        }
    }, [regionNumber, imageRef, completedCrop, crop, getCroppedImage, onOptionalSliceChange, slicePreviewUrl]);

    /**
     * Handle optional slice setting changes
     */
    const handleSliceChange = useCallback((field, value) => {
        console.log(`RegionOptionalSlices ${regionNumber}: ${field} changed to:`, value);

        if (onOptionalSliceChange) {
            onOptionalSliceChange(field, value);
        }
    }, [regionNumber, onOptionalSliceChange]);

    // Don't render if no optional slices are available for this region
    if (!canShowMentalForamen && !canShowLingualCanal && !canShowNasopalatine) {
        return null;
    }

    /**
     * Render individual optional slice section
     */
    const renderOptionalSliceSection = (sliceType, title, fieldName, isVisible) => {
        const defaultText = getDefaultSliceText(sliceType, regionNumber);

        return (
            <Box sx={REGION_FORM_STYLES.sectionStyle} key={sliceType}>
                <Typography sx={REGION_FORM_STYLES.sectionTitleStyle}>
                    {title}
                </Typography>

                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Is an optional {title.toLowerCase()} needed?</InputLabel>
                    <Select
                        value={isVisible || false}
                        onChange={(e) => handleSliceChange(fieldName, e.target.value)}
                        label={`Is an optional ${title.toLowerCase()} needed?`}
                        disabled={disabled}
                    >
                        <MenuItem value={false}>No</MenuItem>
                        <MenuItem value={true}>Yes</MenuItem>
                    </Select>
                </FormControl>

                {isVisible && (
                    <Card variant="outlined" sx={{ mt: 2 }}>
                        <CardContent>
                            <Typography variant="subtitle2" gutterBottom>
                                Upload a new PDF for the slice:
                            </Typography>
                            <Button
                                variant="contained"
                                component="label"
                                disabled={isConverting}
                                startIcon={isConverting ? <CircularProgress size={20} /> : null}
                            >
                                {isConverting ? 'Converting...' : (slicePdfFile ? 'Replace PDF' : 'Choose PDF')}
                                <input
                                    type="file"
                                    hidden
                                    accept="application/pdf"
                                    onChange={handlePdfFileChange}
                                />
                            </Button>
                            {slicePdfFile && !isConverting && <Typography sx={{ ml: 2, display: 'inline' }}>{slicePdfFile.name}</Typography>}

                            {conversionError && <Alert severity="error" sx={{ mt: 2 }}>{conversionError}</Alert>}

                            {sliceImages.length > 0 && (
                                <>
                                    {error && (
                                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                                            {error}
                                        </Alert>
                                    )}

                                    {sliceImages.length > 1 && (
                                        <Box sx={{ my: 2 }}>
                                            <Typography gutterBottom>Select Page ({currentSliceImageIndex + 1} of {sliceImages.length})</Typography>
                                            <Slider
                                                value={currentSliceImageIndex}
                                                onChange={handleSliderChange}
                                                aria-labelledby="slice-image-slider"
                                                valueLabelDisplay="auto"
                                                step={1}
                                                marks
                                                min={0}
                                                max={sliceImages.length - 1}
                                                disabled={disabled}
                                            />
                                        </Box>
                                    )}

                                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                                        Select and crop the {title.toLowerCase()} area:
                                    </Typography>

                                    <Box sx={{ mb: 2 }}>
                                        <ReactCrop
                                            crop={crop || DEFAULT_CROP}
                                            onChange={onCropChange || (() => { })}
                                            onComplete={onCropComplete || (() => { })}
                                            disabled={disabled}
                                        >
                                            <img
                                                src={`data:image/png;base64,${sliceImages[currentSliceImageIndex]}`}
                                                alt={title}
                                                onLoad={onImageLoad}
                                                style={{ maxWidth: '100%', height: 'auto' }}
                                            />
                                        </ReactCrop>
                                    </Box>

                                    <Button
                                        variant="contained"
                                        onClick={() => handleOptionalSlice(sliceType)}
                                        disabled={disabled || processing || (!completedCrop && !crop)}
                                        sx={{ mb: 2 }}
                                        fullWidth
                                    >
                                        {processing ? 'Processing...' : `Set ${title} Slice`}
                                    </Button>

                                    {slicePreviewUrl && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="subtitle2">Slice Preview:</Typography>
                                            <img src={slicePreviewUrl} alt="Slice preview" style={{ maxWidth: '100%', border: '1px solid #ccc', marginTop: '8px' }} />
                                        </Box>
                                    )}

                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={2}
                                        label={`${title} Text`}
                                        value={optionalSliceData.sliceContent || defaultText}
                                        onChange={(e) => handleSliceChange('sliceContent', e.target.value)}
                                        disabled={disabled}
                                        sx={{ mt: 2 }}
                                        placeholder={defaultText}
                                    />

                                    {optionalSliceData.sliceImageFile && (
                                        <Box sx={{ mt: 2, p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                                            <Typography variant="caption" color="success.main">
                                                ✓ {title} slice image captured ({Math.round(optionalSliceData.sliceImageFile.size / 1024)} KB)
                                            </Typography>
                                        </Box>
                                    )}
                                </>
                            )}
                        </CardContent>
                    </Card>
                )}
            </Box>
        );
    };

    return (
        <Box>
            {/* Mental Foramen Section */}
            {canShowMentalForamen && renderOptionalSliceSection(
                'mentalForamen',
                'MENTAL FORAMEN SLICE',
                'showMentalForamen',
                optionalSliceData.showMentalForamen
            )}

            {/* Lingual Canal Section */}
            {canShowLingualCanal && renderOptionalSliceSection(
                'lingualCanal',
                'LINGUAL CANAL SLICE',
                'showLingualCanal',
                optionalSliceData.showLingualCanal
            )}

            {/* Nasopalatine Canal Section */}
            {canShowNasopalatine && renderOptionalSliceSection(
                'nasopalatine',
                'NASOPALATINE CANAL/INCISIVE NERVE SLICE',
                'showNasopalatine',
                optionalSliceData.showNasopalatine
            )}

            {/* Optional Slices Summary */}
            {(canShowMentalForamen || canShowLingualCanal || canShowNasopalatine) && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Optional Slices Summary:
                    </Typography>
                    <Typography variant="caption" display="block">
                        Available slice types: {[
                            canShowMentalForamen && 'Mental Foramen',
                            canShowLingualCanal && 'Lingual Canal',
                            canShowNasopalatine && 'Nasopalatine Canal'
                        ].filter(Boolean).join(', ')}
                    </Typography>
                    <Typography variant="caption" display="block">
                        Active slices: {[
                            optionalSliceData.showMentalForamen && 'Mental Foramen',
                            optionalSliceData.showLingualCanal && 'Lingual Canal',
                            optionalSliceData.showNasopalatine && 'Nasopalatine Canal'
                        ].filter(Boolean).join(', ') || 'None'}
                    </Typography>
                    <Typography variant="caption" display="block">
                        Slice image captured: {optionalSliceData.sliceImageFile ? '✓ Yes' : '○ No'}
                    </Typography>
                </Box>
            )}

            {/* Hidden canvas for image processing */}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
        </Box>
    );
};

RegionOptionalSlices.propTypes = {
    regionNumber: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]).isRequired,
    crop: PropTypes.shape({
        unit: PropTypes.string,
        width: PropTypes.number,
        height: PropTypes.number,
        x: PropTypes.number,
        y: PropTypes.number
    }),
    completedCrop: PropTypes.shape({
        unit: PropTypes.string,
        width: PropTypes.number,
        height: PropTypes.number,
        x: PropTypes.number,
        y: PropTypes.number
    }),
    optionalSliceData: PropTypes.shape({
        showMentalForamen: PropTypes.bool,
        showLingualCanal: PropTypes.bool,
        showNasopalatine: PropTypes.bool,
        sliceContent: PropTypes.string,
        sliceImageFile: PropTypes.object,
        sliceType: PropTypes.string
    }),
    onCropChange: PropTypes.func,
    onCropComplete: PropTypes.func,
    onOptionalSliceChange: PropTypes.func,
    disabled: PropTypes.bool
};

export default RegionOptionalSlices;
